import { useMemo } from 'react'
import { findStoreGroup, toStoreGroups } from '@/domain/store-group'
import { useSelectEcommerce, useSelectGroup } from './use-select-ecommerce'
import { useAppSelector } from '@/redux/hooks'

export type WarehousesScope =
  | { kind: 'group'; group: string; stores: string[] }
  | { kind: 'store'; group: string; stores: string[]; ecommerceId: string }
  | { kind: 'none' }

// Follows the selector: a store shows its own view, a group (or a user's only group) the group's.
export function useWarehousesScope() {
  const user = useAppSelector((s) => s.userApi.getMe.response.user)
  const selected = useAppSelector((s) => s.generalData.selectedEcommerce)
  const selectedGroup = useAppSelector((s) => s.generalData.selectedGroup)
  const selectEcommerce = useSelectEcommerce()
  const selectGroup = useSelectGroup()

  // Empty labels drop the staff fallback ("*") and unnamed groups, which no group-level endpoint answers.
  const groups = useMemo(() => toStoreGroups(user, '', ''), [user])

  const scope = useMemo((): WarehousesScope => {
    const group = selected
      ? findStoreGroup(groups, selected)
      : selectedGroup
        ? groups.find((g) => g.id === selectedGroup)
        : groups.length === 1
          ? groups[0]
          : undefined
    if (!group?.id) return { kind: 'none' }
    if (selected) {
      return {
        kind: 'store',
        group: group.id,
        stores: group.ecommerces,
        ecommerceId: selected
      }
    }
    return { kind: 'group', group: group.id, stores: group.ecommerces }
  }, [groups, selected, selectedGroup])

  // The selector shows a user's only group as no selection, so that is how it is picked.
  const showGroup = () => {
    if (scope.kind === 'none') return
    if (groups.length === 1) selectEcommerce(undefined)
    else selectGroup(scope.group)
  }

  return { scope, showGroup, showStore: selectEcommerce }
}
